import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid, useGridApiRef, type GridRowSelectionModel, type GridPaginationModel, type GridSortModel } from "@mui/x-data-grid";
import { toGridColumns, columnField, resolveRowId, childrenToColumns, flattenColumnGroups, pinnedLeftClass, pinnedRightClass, visibleRowsForFilters, ANTD_FILTER_OPERATOR, type AntdColumn, type FilterControl } from "./columnAdapter";
import type { FilterValue } from "./ColumnFilterButton";
import { tableToCsv, downloadCsv } from "./exportTable";
import { ExpandableTable } from "./ExpandableTable";

export type { AntdColumn };

// antd <Table.Column>/<Table.ColumnGroup> declarative API — metadata holders
// read by DataTable when the `columns` prop isn't used.
export const Column = (_props: AntdColumn): null => null;
export const ColumnGroup = (_props: { title?: ReactNode; children?: ReactNode }): null => null;

// antd <Table.Summary>/<Summary.Row>/<Summary.Cell> footer primitives -> flex rows.
// DataTable publishes the rendered column layout here so a summary cell can size
// itself to the column its `index` names — without it the cells would divide the
// row evenly and sit under the wrong columns — and so it can pin the same columns
// the grid pins.
interface SummaryLayout {
  widths: number[];
  pinnedLeftColumns: number;
  pinnedRightColumns: number;
}
const SummaryLayoutContext = createContext<SummaryLayout | undefined>(undefined);

const sumWidths = (widths: number[]) => widths.reduce((total, w) => total + w, 0);

/** The sticky offset for a pinned summary cell, mirroring `pinnedSx`: the running
 *  width of the columns before it (left) or after it (right). Undefined for an
 *  unpinned cell, which scrolls with the rest of the footer. */
const pinnedOffset = (
  layout: SummaryLayout,
  index: number,
  span: number,
): { left: number } | { right: number } | undefined => {
  const { widths, pinnedLeftColumns, pinnedRightColumns } = layout;
  if (index < pinnedLeftColumns) return { left: sumWidths(widths.slice(0, index)) };
  if (pinnedRightColumns && index >= widths.length - pinnedRightColumns) {
    return { right: sumWidths(widths.slice(index + span)) };
  }
  return undefined;
};

const SummaryCell = ({ children, index, align, colSpan, className }: { children?: ReactNode; index?: number; align?: "left" | "center" | "right"; colSpan?: number; className?: string }) => {
  const layout = useContext(SummaryLayoutContext);
  const span = colSpan ?? 1;
  const width =
    layout && index !== undefined
      ? sumWidths(layout.widths.slice(index, index + span))
      : undefined;
  // The grid pins its first N / last M columns with position:sticky, so those
  // cells do NOT move with its scrollLeft. The footer is shifted wholesale by
  // that same scrollLeft, so its pinned cells have to stay put too — otherwise
  // the label column scrolls out of view and every value lands under a column it
  // does not belong to.
  const pinned =
    layout && index !== undefined ? pinnedOffset(layout, index, span) : undefined;
  return (
    <Box
      className={className}
      // Geometry goes inline rather than through `sx`: these values are computed
      // per cell, so emotion would mint a class per distinct width — and a
      // returns-stream footer has a column per month.
      style={{
        // No published layout (a bare <Summary> outside a grid) -> antd's even split.
        ...(width ? { flex: `0 0 ${width}px`, width } : { flex: span }),
        ...(pinned
          ? {
              position: "sticky",
              ...pinned,
              zIndex: 2,
              // Take the row's own background so scrolled cells pass behind
              // rather than through. A summary row that pins therefore has to
              // set a background (both call sites use `bg-grey-10`).
              backgroundColor: "inherit",
              // The row centres its cells, so an EMPTY pinned cell is only its
              // padding tall and the values passing behind show above and below
              // it. Fill the row's height so the background covers them.
              alignSelf: "stretch",
            }
          : {}),
        textAlign: align,
      }}
      // Match .MuiDataGrid-cell's 4px inset so summary values line up with the
      // line items above them.
      sx={{ px: "4px", py: 0.5 }}
    >
      {children}
    </Box>
  );
};
const SummaryRow = ({ children, className, "data-cy": dataCy }: { children?: ReactNode; className?: string; "data-cy"?: string }) => (
  <Box className={className} data-cy={dataCy} sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: "divider" }}>{children}</Box>
);
const Summary = Object.assign(
  ({ children }: { children?: ReactNode; fixed?: boolean }) => <Box>{children}</Box>,
  { Row: SummaryRow, Cell: SummaryCell },
);
export { Summary };
export type { ColumnsType, ColumnType } from "./columnAdapter";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface AntdPagination {
  pageSize?: number;
  current?: number;
  total?: number;
  position?: string[];
  showSizeChanger?: boolean;
  showTotal?: (total: number, range?: [number, number]) => ReactNode;
  [k: string]: any;
}
interface AntdRowSelection<T> {
  selectedRowKeys?: Array<string | number>;
  onChange?: (keys: Array<string | number>, rows: T[]) => void;
  type?: "checkbox" | "radio";
  [k: string]: any;
}

// antd <Table> -> MUI DataGrid (Community). Covers the features used across the
// grids: columns/dataSource/rowKey/render/sorter, pagination (false or object),
// rowSelection, loading, size, onChange. Advanced antd features degrade rather
// than break; CSV export via exportFileName.
export interface DataTableProps<T = any> {
  columns?: AntdColumn<T>[];
  /** antd declarative <Column>/<ColumnGroup> children (alternative to `columns`). */
  children?: ReactNode;
  dataSource?: T[];
  rowKey?: string | ((r: T) => string | number);
  loading?: boolean;
  pagination?: false | AntdPagination;
  rowSelection?: AntdRowSelection<T>;
  onChange?: (pagination: any, filters: any, sorter: any) => void;
  size?: "small" | "middle" | "large";
  bordered?: boolean;
  tableLayout?: "auto" | "fixed";
  scroll?: { x?: number | string | true; y?: number | string };
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index?: number) => { onClick?: () => void; onMouseEnter?: () => void; onMouseLeave?: () => void; [k: string]: any };
  components?: any;
  summary?: (data: readonly T[]) => ReactNode;
  sticky?: boolean;
  /** antd expandable — Community DataGrid has no built-in expandable rows; accepted for compat. */
  expandable?: any;
  title?: () => ReactNode;
  height?: number | string;
  /** Opt-in fixed data-row height (px). Undefined -> density default. Lets a dense
   *  grid (asset-allocation) match antd's ~30px rows without affecting other call sites. */
  rowHeight?: number;
  /** Opt-in per-header-row height (px). Undefined -> DataGrid default (56). */
  columnHeaderHeight?: number;
  /** Opt-in: remove column resize handles (v8 Community enables them by default). */
  disableColumnResize?: boolean;
  /** Opt-in: pin the first N columns to the left (sticky) on horizontal scroll.
   *  Community DataGrid has no native column pinning — this is a CSS-sticky shim. */
  pinnedLeftColumns?: number;
  /** Opt-in: pin the last M columns to the right (sticky) on horizontal scroll.
   *  Symmetric mirror of pinnedLeftColumns (same CSS-sticky shim). */
  pinnedRightColumns?: number;
  /** Opt-in antd width parity: treat each column's declared `width` as a ratio with a
   *  floor (flex + minWidth) so the columns absorb the container's leftover width
   *  instead of leaving dead space to the right of the last one. Off by default —
   *  grids with deliberate fixed-px columns (allocation spacers) must not stretch. */
  fillWidth?: boolean;
  style?: React.CSSProperties;
  className?: string;
  /** shows an Export CSV button that downloads current rows under this name. */
  exportFileName?: string;
  /** @deprecated use pagination */
  pageSize?: number;
  checkboxSelection?: boolean;
  onSelectionChange?: (keys: Array<string | number>) => void;
  "data-cy"?: string;
  "data-testid"?: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// Stable empty array so `selectedFor` doesn't hand a new reference to every render.
export const isTextEntryTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable);

const EMPTY_FILTER: FilterValue[] = [];
const omitKey = <V,>(obj: Record<string, V>, key: string): Record<string, V> => {
  const { [key]: _dropped, ...rest } = obj;
  return rest;
};

const selectionToKeys = (model: GridRowSelectionModel): Array<string | number> => {
  if (model && typeof model === "object" && "ids" in model) {
    return Array.from((model as { ids: Set<string | number> }).ids);
  }
  return model as unknown as Array<string | number>;
};

// antd <Table> defaults rowKey to "key", but MUI DataGrid REQUIRES a resolvable
// unique id. Prefer `key` (antd default), fall back to `id` (entity grids that
// have no `key`), so neither grid style crashes.
const keyOrId = <T,>(r: T): string | number =>
  ((r as Record<string, unknown>)?.key ?? (r as Record<string, unknown>)?.id) as string | number;

export const DataTable = <T,>({
  columns,
  dataSource = [],
  rowKey = keyOrId,
  loading,
  title,
  pagination,
  rowSelection,
  size,
  scroll,
  height,
  rowHeight,
  columnHeaderHeight,
  disableColumnResize,
  pinnedLeftColumns,
  pinnedRightColumns,
  fillWidth,
  style,
  className,
  exportFileName,
  pageSize,
  checkboxSelection,
  onSelectionChange,
  rowClassName,
  onRow,
  children,
  expandable,
  bordered,
  onChange,
  summary,
  "data-cy": dataCy,
  "data-testid": dataTestid,
}: DataTableProps<T>) => {
  const { cols, grouping } = useMemo(() => {
    // Flatten antd grouped columns (nested `children`) into unique-field leaves +
    // a MUI columnGroupingModel (allocation grid: period → additions/redemptions).
    if (columns) {
      const flat = flattenColumnGroups(columns);
      return { cols: flat.columns as AntdColumn<T>[], grouping: flat.grouping };
    }
    const extracted = childrenToColumns(children);
    return { cols: extracted.columns as AntdColumn<T>[], grouping: extracted.grouping };
  }, [columns, children]);

  // antd column filters: the selection lives here, the affordance is rendered by the
  // adapter, and MUI does the actual filtering through a custom operator. Keyed by
  // column field; a column with no entry is unfiltered.
  const [columnFilters, setColumnFilters] = useState<Record<string, FilterValue[]>>({});
  const filterControl: FilterControl = useMemo(
    () => ({
      selectedFor: (field) => columnFilters[field] ?? EMPTY_FILTER,
      onChangeFor: (field) => (next) =>
        setColumnFilters((prev) => (next.length ? { ...prev, [field]: next } : omitKey(prev, field))),
    }),
    [columnFilters],
  );
  const filterModel = useMemo(
    () => ({
      items: Object.entries(columnFilters)
        .filter(([, value]) => value.length)
        // Separate columns AND together (MUI's default logicOperator), which is antd's
        // behaviour; the OR within one column lives inside the operator itself.
        .map(([field, value]) => ({ id: field, field, operator: ANTD_FILTER_OPERATOR, value })),
    }),
    [columnFilters],
  );

  // Community DataGrid has no expandable/tree rows — delegate to a MUI <Table>
  // implementation that supports antd's expandable (expandedRowRender + tree children).
  if (expandable) {
    return (
      <ExpandableTable<T>
        columns={cols}
        dataSource={dataSource}
        rowKey={rowKey}
        expandable={expandable}
        size={size}
        pagination={pagination}
        onChange={onChange}
        rowClassName={rowClassName}
        onRow={onRow}
        style={style}
        className={className}
        data-cy={dataCy}
        data-testid={dataTestid}
      />
    );
  }
  const gridColumns = useMemo(() => toGridColumns(cols, pinnedLeftColumns, pinnedRightColumns, fillWidth, filterControl), [cols, pinnedLeftColumns, pinnedRightColumns, fillWidth, filterControl]);
  const getRowId = useMemo(() => resolveRowId(rowKey), [rowKey]);
  // antd `defaultSortOrder`: seed the grid's initial sort (lets a page restore a sort
  // it kept in the URL; the grid remounts on navigation, so initial state suffices).
  const defaultSortIdx = cols.findIndex(c => c.defaultSortOrder);
  const initialSorting = defaultSortIdx >= 0
    ? { sorting: { sortModel: [{ field: columnField(cols[defaultSortIdx], defaultSortIdx), sort: cols[defaultSortIdx].defaultSortOrder === "ascend" ? "asc" as const : "desc" as const }] } }
    : {};
  const exportCols = columns ?? cols;

  // P3 left-pin: sticky the first N columns' cells + headers at a running `left` offset
  // (sum of preceding pinned widths). Background is `background.default` — the grid/row/
  // header surface — so horizontally-scrolled content never bleeds through the pinned
  // cells. ponytail: offsets are static from the declared widths; if a pinned column is
  // user-resized the offset won't follow (edge case, acceptable for a shim).
  const pinnedSx = useMemo(() => {
    if (!pinnedLeftColumns && !pinnedRightColumns) return undefined;
    const rules: Record<string, Record<string, unknown>> = {};
    let left = 0;
    for (let i = 0; pinnedLeftColumns && i < pinnedLeftColumns && i < gridColumns.length; i++) {
      rules[`& .MuiDataGrid-cell.${pinnedLeftClass(i)}`] = { position: "sticky", left, zIndex: 3, backgroundColor: "background.default" };
      rules[`& .MuiDataGrid-columnHeader.${pinnedLeftClass(i)}`] = { position: "sticky", left, zIndex: 5, backgroundColor: "background.default" };
      // fillWidth columns carry minWidth instead of width; the rendered width can
      // exceed it, so stacking >1 pinned column under fillWidth under-offsets.
      // ponytail: fine for the single-pinned-column call sites we have.
      const w = gridColumns[i].width ?? gridColumns[i].minWidth;
      left += typeof w === "number" ? w : 0;
    }
    // Mirror for the right: walk from the last column inward, accumulating `right`
    // from the trailing columns' widths so multiple pinned-right columns stack.
    let right = 0;
    for (let i = gridColumns.length - 1; pinnedRightColumns && i >= gridColumns.length - pinnedRightColumns && i >= 0; i--) {
      rules[`& .MuiDataGrid-cell.${pinnedRightClass(i)}`] = { position: "sticky", right, zIndex: 3, backgroundColor: "background.default" };
      rules[`& .MuiDataGrid-columnHeader.${pinnedRightClass(i)}`] = { position: "sticky", right, zIndex: 5, backgroundColor: "background.default" };
      const w = gridColumns[i].width ?? gridColumns[i].minWidth;
      right += typeof w === "number" ? w : 0;
    }
    return rules;
  }, [pinnedLeftColumns, pinnedRightColumns, gridColumns]);

  // antd rendered `summary` as a footer inside the table's own horizontal scroll
  // region. MUI's grid scrolls internally, so the footer lives beside the grid and
  // mirrors its scrollLeft; the column layout goes to the summary cells via
  // context so each one sits under — and pins with — the column its `index` names.
  // ponytail: assumes no checkbox column — neither summary call site selects rows.
  const summaryLayout = useMemo(
    () => ({
      widths: gridColumns.map(col => (typeof col.width === "number" ? col.width : col.minWidth ?? 100)),
      pinnedLeftColumns: pinnedLeftColumns ?? 0,
      pinnedRightColumns: pinnedRightColumns ?? 0,
    }),
    [gridColumns, pinnedLeftColumns, pinnedRightColumns],
  );
  // antd handed `summary` the current page's data, i.e. the rows on screen. The
  // column filters narrow that set, so summarise the visible rows rather than
  // every row handed in — otherwise filtering to three peers still reports
  // statistics over all of them.
  const visibleRows = useMemo(
    () => (summary ? visibleRowsForFilters(cols, columnFilters, dataSource) : dataSource),
    [summary, cols, columnFilters, dataSource],
  );
  const apiRef = useGridApiRef();
  const summaryScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!summary) return undefined;
    return apiRef.current?.subscribeEvent("scrollPositionChange", ({ left }) => {
      if (summaryScrollRef.current) summaryScrollRef.current.scrollLeft = left;
    });
  }, [apiRef, summary]);

  const paginationEnabled = pagination !== false;
  // MUI X Community (MIT) throws "`pageSize` cannot exceed 100" for any pageSize > 100 when
  // pagination is ENABLED. Clamp the enabled-path page size to that ceiling (we are not
  // upgrading to Pro/Premium). The "show all rows" path below is exempt — it uses MUI's
  // ALL_RESULTS sentinel, which the >100 check ignores.
  const MAX_PAGE_SIZE = 100;
  const effectivePageSize = Math.min((pagination && pagination.pageSize) || pageSize || 25, MAX_PAGE_SIZE);
  // Community DataGrid FORCES `pagination: true` (a forced prop — it cannot be turned off).
  // So antd's `pagination={false}` can't disable the pagination *feature*; instead we put the
  // grid on MUI's ALL_RESULTS page (pageSize -1) so it renders EVERY row on one page —
  // reachable by vertical scroll — with the footer hidden. -1 is MUI's documented "All"
  // sentinel: it skips row slicing (gridPaginationSelector: lastRowIndex = rowCount-1) AND is
  // exempt from the >100 throw (`-1 > 100` is false). So >100-row grids (asset-allocation)
  // show ALL rows via scroll, not capped/paged, not crashed. The earlier code used
  // pageSize = dataSource.length here — fine <=100, but it threw once a grid exceeded 100.
  const ALL_ROWS_PAGE_SIZE = -1;
  const showCheckbox = checkboxSelection || Boolean(rowSelection);
  const emitSelection = onSelectionChange ?? (rowSelection?.onChange ? (keys: Array<string | number>) => rowSelection.onChange?.(keys, []) : undefined);
  // antd `scroll={{ y }}` = a bounded body-scroll region (sticky header, rows scroll
  // inside). The adapter had dropped scroll.y, leaving the grid in autoHeight; combined
  // with the forced pagination above that made later rows unreachable. Treat scroll.y as
  // the container height so the grid gets its own vertical scroller (height wins if set).
  const resolvedHeight = height ?? scroll?.y;
  const autoHeight = resolvedHeight === undefined;

  // antd <Table onChange(pagination, filters, sorter)> parity. MUI DataGrid emits
  // paging/sort through separate model callbacks; translate each back to antd's
  // shape and forward to onChange. When the caller drives pagination server-side
  // (a `total` is supplied alongside onChange), switch the grid to server mode so
  // it doesn't try to page/sort only the single page of rows it was handed — that
  // was the regression: onChange was never called on the non-expandable path, so
  // server-paged grids (AFS holdings, PME cashflows) silently stopped paging/sorting.
  const controlledPagination = pagination && typeof pagination === "object" ? pagination : undefined;
  const serverMode = Boolean(onChange) && controlledPagination?.total !== undefined;
  // MUI clamps a controlled page when rowCount shrinks below it. With a
  // cache-and-network query, `total` flickers to 0 mid-page-load, which would
  // clamp the page back and cancel the paging. Hold the last known positive total
  // so rowCount stays stable during the loading transition (MUI's documented
  // server-mode pattern).
  const lastTotalRef = useRef(0);
  if (controlledPagination?.total) lastTotalRef.current = controlledPagination.total;
  const effectiveRowCount = controlledPagination?.total || lastTotalRef.current;
  const emitPageChange = onChange
    ? (model: GridPaginationModel) => onChange({ ...controlledPagination, current: model.page + 1, pageSize: model.pageSize }, {}, {})
    : undefined;
  const emitSortChange = onChange
    ? (model: GridSortModel) => {
        const s = model[0];
        const order = s?.sort === "asc" ? "ascend" : s?.sort === "desc" ? "descend" : null;
        // call sites read the sort column via either `field` (AfsTab) or `columnKey`
        // (PmeCashflows) — antd's SorterResult exposes both, so set both.
        // A cleared sort reports `order: null` (antd parity) so callers can tell it from a page change.
        onChange({ ...controlledPagination }, {}, s ? { field: s.field, columnKey: s.field, order } : { order: null });
      }
    : undefined;

  return (
    <Box sx={{ width: "100%", ...(scroll?.x ? { overflowX: "auto" } : {}) }} style={style} className={className} data-cy={dataCy} data-testid={dataTestid}>
      {title ? <Box sx={{ py: 1, fontWeight: 500 }}>{title()}</Box> : null}
      {exportFileName ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadCsv(exportFileName, tableToCsv(exportCols, dataSource))}>
            Export CSV
          </Button>
        </Stack>
      ) : null}
      <Box sx={{ width: "100%", ...(autoHeight ? {} : { height: resolvedHeight }) }}>
        <DataGrid
          apiRef={apiRef}
          // Make each cell a positioning context so an onCell full-bleed background
          // (group/subtotal band) fills only its own cell instead of escaping to the
          // grid root. Header cells are already position:relative in MUI.
          sx={{
            "& .MuiDataGrid-cell": {
              position: "relative",
              // Match the header inset (4px) so a normal-flow cell (text sits INSIDE this
              // padding) and a full-bleed cell (absolute inset:0, escapes this padding and
              // re-applies 4px in fullBleedStyle) land on the SAME edge — otherwise MUI's
              // default 10px makes right-aligned summary rows sit ~4px right of line items.
              paddingLeft: "4px",
              paddingRight: "4px",
              // antd `bordered` tables show grey horizontal + vertical grid lines; MUI's
              // defaults render near-white. Draw them from the theme divider so EVERY
              // bordered grid matches (not just the asset-allocation one).
              ...(bordered ? { borderRight: 1, borderBottom: 1, borderColor: "divider" } : {}),
            },
            // Cells are a line box, not flex: an inline button sits on the text baseline
            // and its padding pushes it past the row bottom (compact rows are 36px, a small
            // outlined button ~31px). Centre it and drop the vertical padding so it fits.
            "& .MuiDataGrid-cell .MuiButton-root": { verticalAlign: "middle", paddingTop: 0, paddingBottom: 0 },
            // MUI's default header padding (~10px each side) is wider than antd's, so
            // headers like "Weight"/"End Allocation" truncate in their columns. Tighten
            // to match antd density so headers fit.
            "& .MuiDataGrid-columnHeader": {
              paddingLeft: "4px",
              paddingRight: "4px",
              ...(bordered ? { borderRight: 1, borderColor: "divider" } : {}),
            },
            ...(bordered
              ? { "& .MuiDataGrid-columnHeaders": { borderBottom: 1, borderColor: "divider" } }
              : {}),
            ...(pinnedSx ?? {}),
          }}
          // antd tables have no per-column "⋮" menu (filter/hide/sort options). MUI adds
          // one to every column, which shows confusing filter affordances on the empty
          // divider/spacer columns. Remove it for antd parity; header-click sort still works.
          disableColumnMenu
          // opt-in (undefined -> DataGrid defaults): a dense grid can pin row/header
          // height + drop resize handles without changing any other table.
          {...(rowHeight !== undefined ? { rowHeight } : {})}
          {...(columnHeaderHeight !== undefined ? { columnHeaderHeight } : {})}
          {...(disableColumnResize ? { disableColumnResize: true } : {})}
          rows={dataSource}
          columns={gridColumns}
          getRowId={getRowId}
          loading={loading}
          // antd <Table loading> rendered a centered spinner (.ant-spin) over the
          // table regardless of row count. MUI DataGrid's default *noRows* loading
          // overlay is a skeleton (no role="progressbar"), so an empty grid mid-load
          // (e.g. peers table while re-fetching with benchmarks) exposed no
          // accessible/queryable loading indicator. Force a centered circular
          // spinner for both states so loading always surfaces a role="progressbar"
          // (the repo-wide loading hook) AND stays visually consistent across every
          // table (a centered spinner, like antd's old .ant-spin) instead of a
          // top gradient bar on some tables and a control-spinner on others.
          // Once a column is filtered, MUI renders its OWN filter icon in the header and
          // clicking it opens the native filter panel — a Columns/Operator/Value builder
          // sitting next to our antd-style dropdown, exposing the internal operator as an
          // editable form. Two affordances for one job, and the panel is what threw
          // "Missing translation for key filterOperatorAntdIn". The dot badge on our own
          // funnel already marks a filtered column, so drop MUI's.
          slots={{ columnHeaderFilterIconButton: () => null }}
          slotProps={{
            loadingOverlay: {
              variant: "circular-progress",
              noRowsVariant: "circular-progress",
            },
          }}
          autoHeight={autoHeight}
          density={size === "small" ? "compact" : size === "large" ? "comfortable" : "standard"}
          checkboxSelection={showCheckbox}
          onRowSelectionModelChange={emitSelection ? (model) => emitSelection(selectionToKeys(model)) : undefined}
          hideFooterPagination={!paginationEnabled}
          hideFooter={!paginationEnabled}
          getRowClassName={
            rowClassName
              ? (params) =>
                  typeof rowClassName === "function"
                    ? rowClassName(params.row as T, params.indexRelativeToCurrentPage ?? 0)
                    : rowClassName
              : undefined
          }
          onRowClick={onRow ? (params) => onRow(params.row as T)?.onClick?.() : undefined}
          // DataGrid treats Space/Home/End/arrows as cell navigation and preventDefaults
          // them, so a text field in a render-only cell (e.g. the AFS mapping typeahead)
          // can't type a space. Typing inside a field is never grid navigation.
          onCellKeyDown={(_params, event) => {
            if (isTextEntryTarget(event.target)) event.defaultMuiPrevented = true;
          }}
          filterModel={filterModel}
          columnGroupingModel={grouping.length ? grouping : undefined}
          // grouped grids (allocation: 12 periods × add/redeem) are wide — render
          // all columns so every period cell is present + editable in tests/use.
          // Pinned tables ALSO need it off: the CSS-sticky pin shim requires the
          // pinned column to stay mounted when scrolled past, which column
          // virtualization would otherwise unmount.
          disableVirtualization={grouping.length > 0 || Boolean(pinnedLeftColumns) || Boolean(pinnedRightColumns)}
          onPaginationModelChange={emitPageChange ?? (() => {})}
          onSortModelChange={emitSortChange}
          initialState={initialSorting}
          {...(serverMode
            ? {
                paginationMode: "server" as const,
                sortingMode: "server" as const,
                rowCount: effectiveRowCount,
                paginationModel: { page: (controlledPagination!.current ?? 1) - 1, pageSize: effectivePageSize },
              }
            : paginationEnabled
              ? { initialState: { ...initialSorting, pagination: { paginationModel: { page: (controlledPagination?.current ?? 1) - 1, pageSize: effectivePageSize } } } }
              : { paginationModel: { page: 0, pageSize: ALL_ROWS_PAGE_SIZE } })}
          pageSizeOptions={paginationEnabled ? [10, 25, 50, 100] : [ALL_ROWS_PAGE_SIZE]}
        />
      </Box>
      {/* No rows on screen -> nothing to summarise: a footer of dashes under the
          loading overlay, or under an empty grid, is just noise. */}
      {summary && visibleRows.length ? (
        <Box ref={summaryScrollRef} sx={{ overflow: "hidden" }} data-testid="datatable-summary">
          <Box sx={{ minWidth: sumWidths(summaryLayout.widths) }}>
            <SummaryLayoutContext.Provider value={summaryLayout}>
              {summary(visibleRows)}
            </SummaryLayoutContext.Provider>
          </Box>
        </Box>
      ) : null}
      {pagination && pagination.showTotal ? (
        <Box data-cy="pagination-total" sx={{ px: 1, py: 0.5, fontSize: 13, color: "text.secondary" }}>
          {(() => {
            // antd showTotal(total, range): `total` is the FULL count (server total when
            // provided, else the loaded rows), NOT the current page length. range is the
            // [first, last] 1-based index shown on this page.
            const total = pagination.total ?? dataSource.length;
            const start = pagination.total ? ((pagination.current ?? 1) - 1) * effectivePageSize + 1 : Math.min(1, dataSource.length);
            const end = pagination.total ? start + dataSource.length - 1 : dataSource.length;
            return pagination.showTotal(total, [start, end]);
          })()}
        </Box>
      ) : null}
    </Box>
  );
};

// Aliases so antd call sites swap by import only (<Table>, ColumnsType, etc.).
export const Table = Object.assign(DataTable, { Column, ColumnGroup, Summary });
export type { AntdColumn as ColumnType2 } from "./columnAdapter";
export type TableColumnsType<T = unknown> = AntdColumn<T>[];
export type TableProps<T = unknown> = DataTableProps<T>;
// antd deep-import type aliases (loose) so call sites drop their antd/es|lib/table imports.
/* eslint-disable @typescript-eslint/no-explicit-any */
export type TablePaginationConfig = AntdPagination;
export type SorterResult<T = any> = { field?: any; order?: "ascend" | "descend" | null; column?: any; columnKey?: any; [k: string]: any };
/* eslint-enable @typescript-eslint/no-explicit-any */

export default DataTable;
